import React, { useState, useEffect, useMemo, useRef } from "react";
import { Redirect } from "react-router-dom";
import {
  Block,
  Button,
  Row,
  Col,
} from "../../../components/Component";
import { Form, FormGroup, Label, Card, Input } from "reactstrap";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { getPublicUrl } from "../../../utils/Utils";

const createEmptyMarketingSettings = () => ({
  trackingGtmId: "",
  trackingMetaPixelId: "",
  trackingTiktokPixelId: "",
  trackingMetaCapiAccessToken: "",
  trackingMetaDatasetQualityToken: "",
  trackingGa4MeasurementId: "",
  contactWhatsappNumber: "",
  recaptchaV3SecretKey: "",
  recaptchaV3SiteKey: "",
  recaptchaV3Disabled: false,
});

function mapRowToMarketingState(setting, settingsObj) {
  if (setting.key === "tracking_gtm_id") {
    settingsObj.trackingGtmId = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "tracking_meta_pixel_id") {
    settingsObj.trackingMetaPixelId = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "tracking_tiktok_pixel_id") {
    settingsObj.trackingTiktokPixelId = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "tracking_meta_capi_access_token") {
    settingsObj.trackingMetaCapiAccessToken = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "tracking_meta_dataset_quality_token") {
    settingsObj.trackingMetaDatasetQualityToken = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "tracking_ga4_measurement_id") {
    settingsObj.trackingGa4MeasurementId = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "contact_whatsapp_number") {
    settingsObj.contactWhatsappNumber = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "recaptcha_v3_secret_key") {
    settingsObj.recaptchaV3SecretKey = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "recaptcha_v3_site_key") {
    settingsObj.recaptchaV3SiteKey = setting.value != null ? String(setting.value).trim() : "";
  } else if (setting.key === "recaptcha_v3_disabled") {
    const val = setting.value;
    settingsObj.recaptchaV3Disabled = val === true || val === "true" || val === 1 || val === "1";
  }
}

function applySavedRowToState(s, next) {
  if (s.key === "tracking_gtm_id") next.trackingGtmId = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "tracking_meta_pixel_id") next.trackingMetaPixelId = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "tracking_tiktok_pixel_id") next.trackingTiktokPixelId = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "tracking_meta_capi_access_token") next.trackingMetaCapiAccessToken = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "tracking_meta_dataset_quality_token") next.trackingMetaDatasetQualityToken = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "tracking_ga4_measurement_id") next.trackingGa4MeasurementId = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "contact_whatsapp_number") next.contactWhatsappNumber = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "recaptcha_v3_secret_key") next.recaptchaV3SecretKey = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "recaptcha_v3_site_key") next.recaptchaV3SiteKey = s.value != null ? String(s.value).trim() : "";
  else if (s.key === "recaptcha_v3_disabled") next.recaptchaV3Disabled = s.value === true || s.value === "true" || s.value === 1;
}

/**
 * Marketing / analytics / reCAPTCHA / public WhatsApp — embedded in Settings hub (Marketing tab).
 */
export function SystemSettingsMarketingPanel() {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(() => createEmptyMarketingSettings());
  const [originalSettings, setOriginalSettings] = useState(() => createEmptyMarketingSettings());
  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const recaptchaSecretInputRef = useRef(null);
  const recaptchaSiteInputRef = useRef(null);

  useEffect(() => {
    const ac = new AbortController();
    fetchSettings(false, ac.signal);
    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async (bustCache = false, signal) => {
    try {
      setLoading(true);
      const url = bustCache ? `/api/v1/settings?_=${Date.now()}` : "/api/v1/settings";
      const response = await http.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(bustCache && { "Cache-Control": "no-cache", Pragma: "no-cache" }),
        },
        signal,
      });

      const raw = response.data;
      let settingsData = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
      if (!settingsData.length && raw?.data?.settings && Array.isArray(raw.data.settings)) {
        settingsData = raw.data.settings;
      }
      if (!settingsData.length && raw?.settings && Array.isArray(raw.settings)) {
        settingsData = raw.settings;
      }

      const settingsObj = createEmptyMarketingSettings();
      settingsData.forEach((setting) => mapRowToMarketingState(setting, settingsObj));

      setSettings(settingsObj);
      setOriginalSettings({ ...settingsObj });

      if (process.env.NODE_ENV === "development") {
        const sk = settingsObj.recaptchaV3SecretKey || "";
        const site = settingsObj.recaptchaV3SiteKey || "";
        console.log("[SystemSettingsMarketingPanel] reCAPTCHA v3 after fetch: secret key length =", sk.length, ", site key length =", site.length);
      }
    } catch (error) {
      if (isAbortError(error)) return;
      console.error("Error fetching settings:", error);
      swal("Error", "Failed to load settings", "error");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const hasMarketingChanges = () =>
    (settings.trackingGtmId || "") !== (originalSettings.trackingGtmId || "") ||
    (settings.trackingMetaPixelId || "") !== (originalSettings.trackingMetaPixelId || "") ||
    (settings.trackingTiktokPixelId || "") !== (originalSettings.trackingTiktokPixelId || "") ||
    (settings.trackingMetaCapiAccessToken || "") !== (originalSettings.trackingMetaCapiAccessToken || "") ||
    (settings.trackingMetaDatasetQualityToken || "") !== (originalSettings.trackingMetaDatasetQualityToken || "") ||
    (settings.trackingGa4MeasurementId || "") !== (originalSettings.trackingGa4MeasurementId || "") ||
    (settings.contactWhatsappNumber || "") !== (originalSettings.contactWhatsappNumber || "") ||
    (settings.recaptchaV3SecretKey || "") !== (originalSettings.recaptchaV3SecretKey || "") ||
    (settings.recaptchaV3SiteKey || "") !== (originalSettings.recaptchaV3SiteKey || "") ||
    settings.recaptchaV3Disabled !== originalSettings.recaptchaV3Disabled;

  const handleSave = async () => {
    try {
      setLoading(true);
      const secretEl = typeof document !== "undefined" ? document.getElementById("recaptchaV3SecretKey") : null;
      const siteEl = typeof document !== "undefined" ? document.getElementById("recaptchaV3SiteKey") : null;
      const recaptchaSecretRaw =
        (secretEl ? secretEl.value : null) ??
        recaptchaSecretInputRef.current?.value ??
        settings.recaptchaV3SecretKey ??
        "";
      const recaptchaSiteRaw =
        (siteEl ? siteEl.value : null) ??
        recaptchaSiteInputRef.current?.value ??
        settings.recaptchaV3SiteKey ??
        "";
      const recaptchaV3SecretKey = typeof recaptchaSecretRaw === "string" ? recaptchaSecretRaw.trim() : "";
      const recaptchaV3SiteKey = typeof recaptchaSiteRaw === "string" ? recaptchaSiteRaw.trim() : "";

      const settingsToUpdate = [
        {
          key: "tracking_gtm_id",
          value: (settings.trackingGtmId || "").trim(),
          label: "Google Tag Manager Container ID",
          description: "GTM container ID (e.g. GTM-XXXXXXX). Leave empty to disable. Used for analytics and conversion tracking on the main site.",
          category: "integration",
          type: "string",
          isPublic: true,
        },
        {
          key: "tracking_meta_pixel_id",
          value: (settings.trackingMetaPixelId || "").trim(),
          label: "Meta (Facebook) Pixel ID",
          description: "Meta Pixel ID for Facebook/Instagram ads conversion tracking (e.g. numeric ID). Leave empty to disable. Loaded only after marketing cookie consent.",
          category: "integration",
          type: "string",
          isPublic: true,
        },
        {
          key: "tracking_tiktok_pixel_id",
          value: (settings.trackingTiktokPixelId || "").trim(),
          label: "TikTok Pixel ID",
          description: "TikTok Pixel ID for ads conversion tracking. Leave empty to disable. Loaded only after marketing cookie consent.",
          category: "integration",
          type: "string",
          isPublic: true,
        },
        {
          key: "tracking_meta_capi_access_token",
          value: (settings.trackingMetaCapiAccessToken || "").trim(),
          label: "Meta Conversions API — Access Token",
          description: "Server-side Conversions API token. Stored securely; never exposed to the frontend. Used to send lead events to Meta for deduplication with the Pixel.",
          category: "integration",
          type: "string",
          isPublic: false,
        },
        {
          key: "tracking_meta_dataset_quality_token",
          value: (settings.trackingMetaDatasetQualityToken || "").trim(),
          label: "Meta Dataset Quality API — Token",
          description: "Token for the Dataset Quality API. Include it in your direct integration to get event match rate and other quality metrics. Stored securely; never exposed to the frontend.",
          category: "integration",
          type: "string",
          isPublic: false,
        },
        {
          key: "tracking_ga4_measurement_id",
          value: (settings.trackingGa4MeasurementId || "").trim(),
          label: "GA4 Measurement ID",
          description: "Google Analytics 4 Measurement ID (e.g. G-XXXXXXXXXX). Leave empty to disable.",
          category: "integration",
          type: "string",
          isPublic: true,
        },
        {
          key: "contact_whatsapp_number",
          value: (settings.contactWhatsappNumber || "").trim(),
          label: "WhatsApp contact number (Inoqare)",
          description: "Number for the WhatsApp button on the site (e.g. 33123456789 with country code, no +). Leave empty to hide the button.",
          category: "integration",
          type: "string",
          isPublic: true,
        },
        {
          key: "recaptcha_v3_secret_key",
          value: recaptchaV3SecretKey,
          label: "reCAPTCHA v3 secret key",
          description: "Server-side secret key for Google reCAPTCHA v3 (siteverify). Used only by the backend on lead submission; never exposed to the frontend. Fallback: RECAPTCHA_V3_SECRET_KEY env var.",
          category: "integration",
          type: "string",
          isPublic: false,
        },
        {
          key: "recaptcha_v3_site_key",
          value: recaptchaV3SiteKey,
          label: "reCAPTCHA v3 site key",
          description: "Site key for reCAPTCHA v3 (for documentation or frontend use). Safe to expose to the frontend.",
          category: "integration",
          type: "string",
          isPublic: true,
        },
        {
          key: "recaptcha_v3_disabled",
          value: !!settings.recaptchaV3Disabled,
          label: "Disable reCAPTCHA v3 for lead forms",
          description: "When enabled, POST /leads accepts requests without reCAPTCHA token (form works while keys are fixed or for testing).",
          category: "integration",
          type: "boolean",
          isPublic: false,
        },
      ];

      const res = await http.put(
        "/api/v1/settings/bulk",
        { settings: settingsToUpdate },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const saved = Array.isArray(res?.data?.data) ? res.data.data : [];
      if (saved.length) {
        setSettings((prev) => {
          const next = { ...prev };
          saved.forEach((s) => applySavedRowToState(s, next));
          return next;
        });
        setOriginalSettings((prev) => {
          const next = { ...prev };
          saved.forEach((s) => applySavedRowToState(s, next));
          return next;
        });
      }

      await fetchSettings(true);
      const secretLen = recaptchaV3SecretKey.length;
      const msg = secretLen
        ? `Settings saved. reCAPTCHA v3 secret key saved (${secretLen} characters). Values reloaded from server.`
        : "Settings saved successfully. Values reloaded from server.";
      swal("Success", msg, "success");
    } catch (error) {
      console.error("Error saving settings:", error);
      const errMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        (error.response?.data && typeof error.response.data === "object" ? JSON.stringify(error.response.data) : null) ||
        error.message ||
        "Failed to save settings";
      const status = error.response?.status;
      swal(
        "Error",
        status ? `[${status}] ${errMsg}` : errMsg,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="alert alert-light border mb-3" role="region">
        <p className="mb-0 text-soft small">
          <strong>Google Places, blur strategy, and lead notification email</strong> are under Settings →{" "}
          <a href={`${getPublicUrl("admin-settings")}?tab=platform`}>Platform &amp; email</a>.
        </p>
      </div>

      <Block>
        <Card className="card-bordered">
          <div className="card-inner">
            <div className="card-head">
              <h5 className="card-title">Contact — WhatsApp (Inoqare)</h5>
            </div>
            <p className="text-soft small mb-3">
              WhatsApp number used for the button on the site (landing footer). Use country code without + (e.g. 33123456789). Leave empty to hide the button.
            </p>
            <Form>
              <Row className="g-3">
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="contactWhatsappNumber">WhatsApp number</Label>
                    <Input
                      type="text"
                      id="contactWhatsappNumber"
                      name="contactWhatsappNumber"
                      value={settings.contactWhatsappNumber || ""}
                      onChange={handleChange}
                      placeholder="e.g. 33123456789"
                      disabled={loading}
                    />
                    <span className="form-note">Exposed on the public site. Leave empty to hide the WhatsApp link in the footer.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <Button
                      color="primary"
                      onClick={handleSave}
                      disabled={loading || !hasMarketingChanges()}
                    >
                      {loading ? "Saving..." : "Save Settings"}
                    </Button>
                    {hasMarketingChanges() && (
                      <span className="text-soft ml-3">
                        You have unsaved changes
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </Card>
      </Block>

      <Block>
        <Card className="card-bordered">
          <div className="card-inner">
            <div className="card-head">
              <h5 className="card-title">Tracking &amp; Analytics (Pixel, GTM, GA4)</h5>
            </div>
            <p className="text-soft small mb-3">
              Configure tracking IDs for the main site. The frontend loads these scripts from the public API. Leave a field empty to disable that tracker. Save once to apply; changes take effect on the next page load.
            </p>
            <Form>
              <Row className="g-3">
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="trackingGtmId">Google Tag Manager Container ID</Label>
                    <Input
                      type="text"
                      id="trackingGtmId"
                      name="trackingGtmId"
                      value={settings.trackingGtmId || ""}
                      onChange={handleChange}
                      placeholder="e.g. GTM-XXXXXXX"
                      disabled={loading}
                    />
                    <span className="form-note">Used for GTM container. Leave empty to disable.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="trackingMetaPixelId">Meta Pixel ID</Label>
                    <Input
                      type="text"
                      id="trackingMetaPixelId"
                      name="trackingMetaPixelId"
                      value={settings.trackingMetaPixelId || ""}
                      onChange={handleChange}
                      placeholder="e.g. 123456789012345"
                      disabled={loading}
                    />
                    <span className="form-note">Numeric Pixel ID for Meta/Facebook ads. Leave empty to disable. Loaded only after the visitor accepts marketing cookies.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="trackingTiktokPixelId">TikTok Pixel ID</Label>
                    <Input
                      type="text"
                      id="trackingTiktokPixelId"
                      name="trackingTiktokPixelId"
                      value={settings.trackingTiktokPixelId || ""}
                      onChange={handleChange}
                      placeholder="e.g. CXXXXXXXXXXXXXXXXX"
                      disabled={loading}
                    />
                    <span className="form-note">TikTok Pixel ID. Leave empty to disable. Loaded only after the visitor accepts marketing cookies.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="trackingMetaCapiAccessToken">Meta Conversions API — Access Token</Label>
                    <Input
                      type="password"
                      id="trackingMetaCapiAccessToken"
                      name="trackingMetaCapiAccessToken"
                      value={settings.trackingMetaCapiAccessToken || ""}
                      onChange={handleChange}
                      placeholder="Server-side CAPI token (stored in DB, never exposed to frontend)"
                      disabled={loading}
                      autoComplete="off"
                    />
                    <span className="form-note">Used by the backend to send lead events to Meta for deduplication with the Pixel. Not exposed to the public site.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="trackingMetaDatasetQualityToken">Meta Dataset Quality API — Token</Label>
                    <Input
                      type="password"
                      id="trackingMetaDatasetQualityToken"
                      name="trackingMetaDatasetQualityToken"
                      value={settings.trackingMetaDatasetQualityToken || ""}
                      onChange={handleChange}
                      placeholder="Dataset Quality API token (for event match rate and quality metrics)"
                      disabled={loading}
                      autoComplete="off"
                    />
                    <span className="form-note">Include this token in your direct integration to get event match rate and other quality indicators. Stored securely; never exposed to the frontend.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="trackingGa4MeasurementId">GA4 Measurement ID</Label>
                    <Input
                      type="text"
                      id="trackingGa4MeasurementId"
                      name="trackingGa4MeasurementId"
                      value={settings.trackingGa4MeasurementId || ""}
                      onChange={handleChange}
                      placeholder="e.g. G-XXXXXXXXXX"
                      disabled={loading}
                    />
                    <span className="form-note">Google Analytics 4 Measurement ID. Leave empty to disable.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <Button
                      color="primary"
                      onClick={handleSave}
                      disabled={loading || !hasMarketingChanges()}
                    >
                      {loading ? "Saving..." : "Save Settings"}
                    </Button>
                    {hasMarketingChanges() && (
                      <span className="text-soft ml-3">
                        You have unsaved changes
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </Card>
      </Block>

      <Block>
        <Card className="card-bordered">
          <div className="card-inner">
            <div className="card-head">
              <h5 className="card-title">reCAPTCHA v3 (anti-bots)</h5>
            </div>
            <p className="text-soft small mb-3">
              Used to verify leads on circle and clinic landings. The <strong>secret key</strong> is used only by the backend (Google siteverify); never expose it to the frontend. The backend uses this value in priority; if empty, it falls back to the <code>RECAPTCHA_V3_SECRET_KEY</code> environment variable.
            </p>
            <Form>
              <Row className="g-3">
                <Col md="12">
                  <FormGroup>
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="recaptchaV3Disabled"
                        name="recaptchaV3Disabled"
                        checked={!!settings.recaptchaV3Disabled}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <Label className="custom-control-label" htmlFor="recaptchaV3Disabled">
                        Disable reCAPTCHA v3 for lead forms
                      </Label>
                    </div>
                    <span className="form-note">
                      When enabled, the backend accepts lead submissions without a reCAPTCHA token so the form works (e.g. while fixing keys or for testing). Re-enable once reCAPTCHA is correctly configured.
                    </span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="recaptchaV3SecretKey">reCAPTCHA v3 secret key</Label>
                    <Input
                      type="password"
                      id="recaptchaV3SecretKey"
                      name="recaptchaV3SecretKey"
                      innerRef={recaptchaSecretInputRef}
                      value={settings.recaptchaV3SecretKey || ""}
                      onChange={handleChange}
                      placeholder="Enter reCAPTCHA v3 secret key (server-side only)"
                      disabled={loading}
                      autoComplete="off"
                    />
                    <span className="form-note">Stored securely; never sent to the frontend or logged. Get keys from Google reCAPTCHA admin.</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="recaptchaV3SiteKey">reCAPTCHA v3 site key (optional)</Label>
                    <Input
                      type="text"
                      id="recaptchaV3SiteKey"
                      name="recaptchaV3SiteKey"
                      innerRef={recaptchaSiteInputRef}
                      value={settings.recaptchaV3SiteKey || ""}
                      onChange={handleChange}
                      placeholder="For documentation or frontend use"
                      disabled={loading}
                    />
                    <span className="form-note">Safe to expose to the frontend. Used for documentation or future front use (e.g. NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY).</span>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <Button
                      color="primary"
                      onClick={handleSave}
                      disabled={loading || !hasMarketingChanges()}
                    >
                      {loading ? "Saving..." : "Save Settings"}
                    </Button>
                    {hasMarketingChanges() && (
                      <span className="text-soft ml-3">
                        You have unsaved changes
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </Card>
      </Block>
    </React.Fragment>
  );
}

export default function SystemSettingsLegacyRedirect() {
  return <Redirect to={{ pathname: getPublicUrl("admin-settings"), search: "?tab=marketing" }} />;
}
